// FILE: assets/bold-form-builder.js

if (!customElements.get('bold-form-builder')) {
  customElements.define(
    'bold-form-builder',
    class BoldFormBuilder extends HTMLElement {
      constructor() {
        super();
        this.steps = [];
        this.currIndex = 0;
        this.isWizard = false;
        this.btnPrev = null;
        this.CONST = {
          BTN_NEXT: 'Customize Your Imprint ›',
          BTN_PREV_TEXT: '‹ Edit Product Options',
          CLS_ACTIVE: 'bold_step_active',
          CLS_ERROR: 'bold_option_error',
          SETS: '.bold_option_set',
          CLS_ORIGINAL_BTN: 'bold-original-submit',
          CLS_NEXT_BTN: 'bold-next-submit'
        };
        
        this._handleFormSubmit = this._handleFormSubmit.bind(this);
      }

      connectedCallback() {
        if (this._booted) return;
        this._booted = true;
        this.isWizard = this.getAttribute('data-wizard') === 'true';

        const adapter = document.querySelector('bold-sc-adapter');
        this.facade = adapter ? adapter.facade : null;
        
        // --- FIX: Simplify Initialization Trigger ---
        const runInitAfterDOM = () => {
            // Use a short, fixed delay (e.g., 50ms) to ensure the DOM rendering cycle finishes
            // after the options data (hub:options:ready) is available.
            setTimeout(() => {
                this.init();
            }, 50);
        };

        if (adapter && adapter.getAttribute('options') === 'ready') {
            runInitAfterDOM();
        } else {
            document.addEventListener('hub:options:ready', () => {
                const tryInit = (attempt = 0) => {
                    const a = document.querySelector('bold-sc-adapter');
                    this.facade = a ? a.facade : null;
                    if (this.facade || attempt >= 5) {
                        runInitAfterDOM();
                    } else {
                        setTimeout(() => tryInit(attempt + 1), 100);
                    }
                };
                tryInit();
            }, { once: true });
        }
        // --- END FIX ---
      }

      init() {
        if (this.hasAttribute('ready')) return;
        // Check for sets now that we've waited a small window after options are ready
        const sets = document.querySelectorAll(this.CONST.SETS);
        if (!sets || sets.length === 0) return;

        this.steps = Array.from(sets);
        this._injectStepHeaders();

        // Identify original buttons for visibility toggling later
        this._identifyMainButtons();

        // *** KEY CHANGE: Attach a CAPTURE listener to the form ***
        // This allows us to intercept the submit BEFORE product-form.js or native logic
        // sees it, but only if we need to (validation failure or wizard navigation).
        const form = this.closest('form');
        if (form) {
            form.addEventListener('submit', this._handleFormSubmit, true); 
        }

        // Facade listeners for price updates
        if (this.facade && this.facade.options.onChange) {
            this.facade.options.onChange((detail) => {
                if (this.isWizard) this.updateState();
                if (detail.formattedTotal) {
                    this._updatePriceDisplay(detail.formattedTotal);
                }
                const target = detail.event && detail.event.target ? detail.event.target.closest('.bold_option') : null;
                if (target) target.classList.remove(this.CONST.CLS_ERROR);
            });
        } else {
            this.addEventListener('change', () => { if (this.isWizard) this.updateState(); });
            this.addEventListener('input', () => { if (this.isWizard) this.updateState(); });
        }

        if (this.facade && this.facade.state && this.facade.state.getFormattedTotal) {
            this._updatePriceDisplay(this.facade.state.getFormattedTotal());
        }

        if (this.isWizard) {
            this.classList.add('mode-wizard');
            this._buildCustomizationButton();
            this._buildNav();
            this.updateState(true);
        } else {
            this.classList.add('mode-standard');
        }

        this.setAttribute('ready', '');
      }

      _identifyMainButtons() {
        const scope = this.closest('.product, product-info, form') || document;
        const btns = scope.querySelectorAll('[type="submit"][name="add"], .product-form__submit, .product-form__buttons button');
        btns.forEach(btn => btn.classList.add(this.CONST.CLS_ORIGINAL_BTN));
      }

      _handleFormSubmit(e) {
          if (!this.isWizard) return;

          const isLast = this.currIndex === this.steps.length - 1;

          if (!isLast) {
              // If hitting "Enter" on an intermediate step, treat as Next
              e.preventDefault();
              e.stopPropagation();
              this.goNext();
          } else {
              // If on the LAST step, run validation.
              if (!this.validateStep(this.currIndex)) {
                  // Invalid: Stop everything.
                  e.preventDefault();
                  e.stopPropagation();
              }
              // Valid: Do nothing. Let the event bubble to product-form.js
          }
      }

      // --- DELETED: _waitForSets function ---

      _buildCustomizationButton() {
        const container = document.querySelector('bold-price-display [data-slot="button-container"]');
        if (!container) return;
        const button = document.createElement('button');
        button.className = `button ${this.CONST.CLS_NEXT_BTN}`;
        button.type = 'button';
        button.textContent = this.CONST.BTN_NEXT;
        
        // Next Button Logic
        button.onclick = (e) => { 
            e.preventDefault(); 
            this.goNext(); 
        };
        
        container.appendChild(button);
      }

      _injectPackageHeaders() {
        const getGarmentLabel = (productNum) => {
          /*
          * Look through all Bold options and find the likely garment color field.
          *
          * Product 1 examples:
          * T-Shirt Color
          * Crewneck Color
          *
          * Product 2 examples:
          * T-Shirt Color #2
          * Crewneck Color #2
          */
          const allOptions = this.steps.flatMap(step =>
            Array.from(step.querySelectorAll('.bold_option'))
          );

          const match = allOptions.find(option => {
            if (option.classList.contains('bold_option_displaytext')) {
              return false;
            }

            const titleEl = option.querySelector('.bold_option_title');
            if (!titleEl) return false;

            const title = (titleEl.textContent || '')
              .replace(/\*/g, '')
              .trim();

            if (!title) return false;

            const isColorField = /\bcolou?r\b/i.test(title);

            if (!isColorField) return false;

            const isProduct2 = /#2\s*$/i.test(title);

            return productNum === 2
              ? isProduct2
              : !isProduct2;
          });

          if (!match) {
            return productNum === 1
              ? 'Product 1'
              : 'Product 2';
          }

          const titleEl = match.querySelector('.bold_option_title');

          const garment = (titleEl.textContent || '')
            .replace(/\*/g, '')
            .replace(/\s*(color|colour)\s*/i, '')
            .replace(/\s*#?\s*2\s*$/i, '')
            .replace(/\s*2\s*$/i, '')
            .trim();

          return garment || (
            productNum === 1
              ? 'Product 1'
              : 'Product 2'
          );
        };

        const garment1 = getGarmentLabel(1);
        const garment2 = getGarmentLabel(2);

        const createHeader = (text, type) => {
          const header = document.createElement('div');

          header.className =
            `bold_option bold_option_displaytext package-section-header package-section-${type}`;

          header.innerHTML = `
            <span class="bold_option_title">${text}</span>
            <span class="bold_option_element">
              <p>${text}</p>
            </span>
          `;

          return header;
        };

        const insertHeaderBeforeOption = (option, text, type) => {
          const optionRow = option.closest('.bold-option-row');

          if (optionRow) {
            optionRow.before(
              createHeader(text, type)
            );
          } else {
            option.before(
              createHeader(text, type)
            );
          }
        };

        let product1Inserted = false;
        let product2Inserted = false;
        let greekInserted = false;

        this.steps.forEach(step => {
          if (!step) return;

          const options = Array.from(
            step.querySelectorAll('.bold_option')
          ).filter(option => {
            return (
              !option.classList.contains('bold_option_displaytext') &&
              !option.classList.contains('package-section-header')
            );
          });

          options.forEach(option => {
            const titleEl = option.querySelector('.bold_option_title');

            if (!titleEl) return;

            const title = (titleEl.textContent || '')
              .replace(/\*/g, '')
              .trim();

            if (!title) return;

            const isGreekLetter =
              /^Greek Letter\s+\d+/i.test(title);

            const isProduct2 =
              /#2\s*$/i.test(title);

            // Greek Letters
            if (isGreekLetter) {
              if (!greekInserted) {
                insertHeaderBeforeOption(
                  option,
                  'Greek Letters',
                  'greek'
                );

                greekInserted = true;
              }

              return;
            }

            // Product 2
            if (isProduct2) {
              if (!product2Inserted) {
                insertHeaderBeforeOption(
                  option,
                  `Product 2 - ${garment2}`,
                  'product-2'
                );

                product2Inserted = true;
              }

              return;
            }

            // Product 1
            if (!product1Inserted) {
              insertHeaderBeforeOption(
                option,
                `Product 1 - ${garment1}`,
                'product-1'
              );

              product1Inserted = true;
            }
          });
        });
      }

      _injectStepHeaders() {
        const isPackage = this.getAttribute('data-package') === 'true';

        if (isPackage) {
          this._injectPackageHeaders();
          return;
        }

        const getColorExcludePhrases = () => {
          const attr = (this.getAttribute('data-color-exclude') || '')
            .split(',')
            .map(s => s.trim().toLowerCase())
            .filter(Boolean);

          const defaults = [
            '# of colors for front design',
            '# of colors for back design',
            '# of colors for left sleeve design',
            '# of colors for right sleeve design',
            'ink colors (front)',
            'ink colors (back)',
            'ink colors (left sleeve)',
            'ink colors (right sleeve)'
          ];

          return Array.from(new Set([...defaults, ...attr]));
        };

        const isExcludedColorText = (text) => {
          const t = String(text || '').toLowerCase();
          return getColorExcludePhrases().some(p => p && t.includes(p));
        };

        const hasRealColorInStep = (stepEl) => {
          if (!stepEl) return false;

          const titles = Array.from(
            stepEl.querySelectorAll('.bold_option_title')
          )
            .filter(el => !el.closest('.bold_option_displaytext'))
            .map(el => (el.textContent || '').trim())
            .filter(Boolean);

          /*
          * Look at each option independently.
          *
          * A step counts as having a real garment color if AT LEAST
          * one title contains "Color/Colour" and that specific title
          * is not one of our excluded "# of colors" fields.
          */
          return titles.some(title => {
            const hasColorWord = /\bcolou?rs?\b/i.test(title);

            if (!hasColorWord) return false;
            if (isExcludedColorText(title)) return false;

            return true;
          });
        };

        const hasOnlySizeInStep = (stepEl) => {
          if (!stepEl) return false;

          const hasBoldSize = !!stepEl.querySelector('bold-quantity-picker');
          if (!hasBoldSize) return false;

          // Count only real visible Bold options.
          // Ignore:
          // - display text headers
          // - the hidden original Size option
          // - generated form-builder headers
          const realOptions = Array.from(
            stepEl.querySelectorAll('.bold_option')
          ).filter(option => {
            if (option.classList.contains('bold_option_displaytext')) {
              return false;
            }

            if (option.classList.contains('bold_option_hidden')) {
              return false;
            }

            if (option.classList.contains('form-builder-step-header')) {
              return false;
            }

            return true;
          });

          return realOptions.length === 0;
        };

        const generateHeader = (text = '', stepNum = 0) => {
          if (!text || text === '') {
            return 'Product Options';
          }

          const normalized = text.toLowerCase();

          let hasMainColor = false;
          let hasSize = false;

          if (normalized.includes('size')) {
            hasSize = true;
          }

          if (normalized.includes('color') && stepNum === 0) {
            hasMainColor = true;
          }

          if (hasSize || hasMainColor) {
            return 'Product Options';
          }

          return 'Customization Options';
        };

        const hasLeadingDisplayText = (stepEl) => {
          if (!stepEl) return false;

          const children = Array.from(stepEl.children);

          /*
          * Look near the beginning of the set.
          * Ignore things like hidden options / generated wrappers,
          * but if Bold supplied a display text before the real
          * customization fields, treat that as the section header.
          */
          const firstRealChildren = children.filter(child => {
            if (child.classList.contains('bold_option_hidden')) return false;
            if (child.classList.contains('form-builder-step-header')) return false;

            return true;
          });

          const firstFew = firstRealChildren.slice(0, 2);

          return firstFew.some(child =>
            child.classList.contains('bold_option_displaytext')
          );
        };

        this.steps.forEach((step, i) => {
          const onlySize = hasOnlySizeInStep(step);
          const hasRealColor = hasRealColorInStep(step);
/*
          console.log('[Header Debug]', {
            stepIndex: i,
            onlySize,
            hasRealColor,
            shouldSkip: onlySize || (i === 0 && !hasRealColor),
            step
          });
*/
          if (hasOnlySizeInStep(step)) {
            return;
          }

          /*
          * Bold already supplied a descriptive section header.
          * Leave it alone and don't inject Product Options /
          * Customization Options over it.
          */
          if (hasLeadingDisplayText(step)) {
            return;
          }

          /*
          * Important:
          * Ignore any existing display text when determining
          * what this step actually contains.
          */
          const textContent = Array.from(
            step.querySelectorAll('.bold_option')
          )
            .filter(option => !option.classList.contains('bold_option_displaytext'))
            .map(option => option.textContent || '')
            .join(' ')
            .toLowerCase();

          const headerTitle = generateHeader(textContent, i);

          /*
          * Only check for a header generated by THIS component.
          *
          * Existing Bold descriptive text such as:
          * "Size & Quantity"
          * "Artwork"
          * etc. should be left completely alone.
          */
          const existingGeneratedHeader = step.querySelector(
            ':scope > .form-builder-step-header'
          );

          if (existingGeneratedHeader) {
            return;
          }

          /*
          * Create our top-level step header.
          */
          const header = document.createElement('div');

          header.className =
            'bold_option bold_option_displaytext form-builder-step-header';

          header.innerHTML = `
            <span class="bold_option_title">${headerTitle}</span>
            <span class="bold_option_element">
              <p>${headerTitle}</p>
            </span>
          `;

          /*
          * Put it at the very beginning of the option set,
          * before any existing Bold descriptive text.
          */
          step.prepend(header);
        });
      }

      _buildNav() {
        if (!this.isWizard) return;
        this.btnPrev = document.createElement('button');
        this.btnPrev.className = 'bold-wizard-prev';
        this.btnPrev.type = 'button';
        this.btnPrev.innerHTML = this.CONST.BTN_PREV_TEXT;
        this.btnPrev.onclick = (e) => { e.preventDefault(); this.goPrev(); };
        const priceDisplayFooter = document.querySelector('bold-price-display .bold-footer-info');
        if (priceDisplayFooter) priceDisplayFooter.prepend(this.btnPrev);
      }

      _updatePriceDisplay(priceString) {
        if (!document.querySelector('bold-price-display')) return;
        const isLast = this.currIndex === this.steps.length - 1;
        if (isLast) {
            const finalBtns = document.querySelectorAll(`bold-price-display .${this.CONST.CLS_ORIGINAL_BTN}`);
            finalBtns.forEach(btn => { 
                // Update text, but don't touch functionality
                const baseText = btn.getAttribute('data-original-text') || 'Add to Cart';
                btn.textContent = `${baseText}`; 
            });
        }
      }

      updateState(reset = false) {
        if (!this.isWizard) return;
        if (reset) this.currIndex = 0;
        
        // 1. Toggle Step Visibility
        this.steps.forEach((step, idx) => {
          const isActive = idx === this.currIndex;
          if (isActive) {
            step.classList.add(this.CONST.CLS_ACTIVE);
            this.setAttribute('data-step-index', idx);
            if (!reset && stepChanged && idx > 0) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            step.classList.remove(this.CONST.CLS_ACTIVE);
          }
        });

        this._updateNavButtons();
        this._updateActionButton();
        this._updateBuyButtonsVisibility();
      }
      
      _updateActionButton() {
          const isLast = this.currIndex === this.steps.length - 1;
          const priceDisplay = document.querySelector('bold-price-display');
          if (!priceDisplay) return;

          const originalBtns = priceDisplay.querySelectorAll(`.${this.CONST.CLS_ORIGINAL_BTN}`);
          const nextBtn = priceDisplay.querySelector(`.${this.CONST.CLS_NEXT_BTN}`);

          const isValid = this.validateStep(this.currIndex, true);
          this._updateProgressText(isValid, isLast);

          // --------------------------------------------------------
          // INTERMEDIATE STEP
          // --------------------------------------------------------
          if (!isLast) {
            // Let the visibility helper hide wrappers + buy buttons on non-final steps
            this._updateBuyButtonsVisibility();

            // Show Next Button
            if (nextBtn) {
              nextBtn.style.display = 'block';
              nextBtn.textContent = this.CONST.BTN_NEXT;

              if (isValid) {
                nextBtn.removeAttribute('disabled');
                nextBtn.style.opacity = '1';
              } else {
                nextBtn.setAttribute('disabled', 'disabled');
                nextBtn.style.opacity = '0.5';
              }
            }
          }
          // --------------------------------------------------------
          // FINAL STEP
          // --------------------------------------------------------
          else {
            // Hide Next Button
            if (nextBtn) nextBtn.style.display = 'none';

            // Show wrapper AND original buy buttons via central helper
            this._updateBuyButtonsVisibility();
          }
      }

      _updateProgressText(isValid, isLast) {
          const footer = document.querySelector('bold-price-display .bold-footer-action');
          if (!footer) return;
          
          let helpText = footer.querySelector('.bold-step-help-text');
          if (!helpText) {
              helpText = document.createElement('div');
              helpText.className = 'bold-step-help-text';
              footer.prepend(helpText);
          }

          if (isLast) {
              helpText.style.display = 'none';
              return;
          }
          helpText.style.display = 'block';

          const step = this.steps[this.currIndex];
          if (!step) return;

          const blocks = Array.from(step.querySelectorAll('.bold_option')).filter(b => 
              !b.classList.contains('bold_option_displaytext')
          );
          
          let optionsReq = 0;
          let optionsFilled = 0;

          blocks.forEach(b => {
              if (b.offsetParent === null) return; 

              const isRequired = b.classList.contains('bold_option_value_required') || 
                                  b.querySelector('[required]') ||
                                  (b.querySelector('label') && b.querySelector('label').innerText.includes('*')) ||
                                  (b.querySelector('.bold_option_title') && b.querySelector('.bold_option_title').innerText.includes('*'));

              if (isRequired) {
                  optionsReq++;
                  if (this._isBlockFilled(b)) optionsFilled++;
              }
          });

          let qtyReq = 0;
          let qtyFilled = 0;
          const sizeGrid = step.querySelector('.bold_option_sizes, [data-size-grid], .quantity-slider-group');
          
          if (sizeGrid && sizeGrid.offsetParent !== null) {
              qtyReq = 1; 
              const lockedGrid = step.querySelector('.bold-qty-placeholder');
              const isLocked = lockedGrid && getComputedStyle(lockedGrid).display !== 'none';

              if (!isLocked) { 
                      const matrixInputs = sizeGrid.querySelectorAll('.matrix-qty-input');
                      let total = 0;
                      if (matrixInputs.length > 0) {
                          total = Array.from(matrixInputs).reduce((sum, el) => sum + (parseInt(el.value) || 0), 0);
                      } else {
                          const qtyInput = document.querySelector('[name="quantity"]');
                          total = qtyInput ? parseInt(qtyInput.value) : 0;
                      }
                      if (total > 0) qtyFilled = 1;
              }
          }

          const totalReq = optionsReq + qtyReq;
          // FIX: Changed 'qtyReq' to 'qtyFilled' for the final tally.
          const totalFilled = optionsFilled + qtyFilled; 

          if (isValid) {
              helpText.textContent = "Step Complete";
              helpText.style.color = "green";
          } else {
              helpText.textContent = `${totalFilled}/${totalReq} Required Fields`;
              helpText.style.color = "#666";
          }
      }

      _updateBuyButtonsVisibility() {
        if (!this.isWizard) return;
        const isLast = this.currIndex === this.steps.length - 1;
        const scope = this.closest('.product, product-info, form') || document;
        const priceDisplay = scope.querySelector('bold-price-display');
        const wrapperSelector = '.product-form__buttons';
        const originalBtnClass = this.CONST.CLS_ORIGINAL_BTN;

        const wrappers = scope.querySelectorAll(wrapperSelector);
        // console.log('updating visib', wrappers);

        wrappers.forEach((wrapper) => {
          // If wrapper lives inside bold-price-display footer, you can optionally
          // skip it (kept commented as in your original):
          // if (priceDisplay && priceDisplay.contains(wrapper.closest('.bold-price-footer'))) return;

          // Toggle the wrapper that contains the buy button(s)
          wrapper.style.display = isLast ? '' : 'none';

          // ALSO explicitly toggle the actual buy buttons inside the wrapper
          const innerButtons = wrapper.querySelectorAll(
            `.${originalBtnClass}, [type="submit"][name="add"], .product-form__submit`
          );

          innerButtons.forEach((btn) => {
            btn.style.display = isLast ? '' : 'none';
          });
        });

        // NEW: hide the wizard "Next" button on the last step
        if (priceDisplay) {
          const nextBtn = priceDisplay.querySelector(`.${this.CONST.CLS_NEXT_BTN}`);
          if (nextBtn) {
            nextBtn.style.display = isLast ? 'none' : 'block';
          }
        }
      }

      _updateNavButtons() {
        if (!this.btnPrev) return;
        this.btnPrev.style.display = this.currIndex === 0 ? 'none' : 'block';
      }

      validateStep(index, silent = false) {
        const step = this.steps[index];
        if (!step) return true;
        let isValid = true;
        
        const lockedPlaceholder = step.querySelector('.bold-qty-placeholder');
        if (lockedPlaceholder && window.getComputedStyle(lockedPlaceholder).display !== 'none') {
            if (!silent) this._highlightError(lockedPlaceholder); 
            return false;
        }

        const sizeGrid = step.querySelector('.bold_option_sizes, [data-size-grid], .quantity-slider-group');
        if (sizeGrid && sizeGrid.offsetParent !== null) {
           const matrixInputs = sizeGrid.querySelectorAll('.matrix-qty-input');
           let total = 0;
           if (matrixInputs.length > 0) {
               total = Array.from(matrixInputs).reduce((sum, el) => sum + (parseInt(el.value) || 0), 0);
           } else {
               const qtyInput = document.querySelector('[name="quantity"]');
               total = qtyInput ? parseInt(qtyInput.value) : 0;
           }
           if (total <= 0) {
               if (!silent) this._highlightError(sizeGrid);
               isValid = false;
           }
        }

        const blocks = Array.from(step.querySelectorAll('.bold_option'));
        blocks.forEach(block => {
            if (block.offsetParent === null) return;
            if (block.classList.contains('bold_option_displaytext')) return;

            const isRequired = block.classList.contains('bold_option_value_required') || block.querySelector('label')?.innerText.includes('*') || block.querySelector('[required]');
            if (isRequired && !this._isBlockFilled(block)) {
                if (!silent) this._highlightError(block);
                isValid = false;
            }
        });

        if (!isValid && !silent) this.handleProductFormError('Please complete required fields.');
        return isValid;
      }

      _isBlockFilled(block) {
        const sel = block.querySelector('select');
        if (sel) return sel.value && sel.value !== '' && !sel.value.startsWith('--');
        const inputs = block.querySelectorAll('input[type="radio"], input[type="checkbox"]');
        if (inputs.length > 0) return Array.from(inputs).some(i => i.checked);
        const text = block.querySelector('input[type="text"], input[type="number"], textarea');
        if (text) return text.value.trim() !== '';
        const file = block.querySelector('input[type="file"]');
        if (file) return file.files && file.files.length > 0;
        return true;
      }

      _highlightError(element) {
        element.classList.add(this.CONST.CLS_ERROR);
        setTimeout(() => element.classList.remove(this.CONST.CLS_ERROR), 5000);
      }
      
      handleProductFormError(msg) {
        const productForm = this.closest('product-form') || document.querySelector('product-form');
        if (productForm && typeof productForm.handleErrorMessage === 'function') { productForm.handleErrorMessage(msg); } else { alert(msg); }
      }

      goNext() { if (this.validateStep(this.currIndex)) { this.currIndex++; this.updateState(); } }
      goPrev() { if (this.currIndex > 0) { this.currIndex--; this.updateState(); } }
    }
  );
}